import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppDialog } from "@/components/common/AppDialog";
import type { MenuCategory } from "@/types/menu-category";

interface DeleteCategoryDialogProps {
  /** The category to delete; the dialog is closed while this is null. */
  category: MenuCategory | null;
  /** The concept's other categories: where its menu items can go. */
  others: MenuCategory[];
  onOpenChange: (open: boolean) => void;
  /** `reassignToId` is set whenever the category has menu items. */
  onConfirm: (category: MenuCategory, reassignToId?: number) => void;
  isPending?: boolean;
  error?: string | null;
}

/**
 * Deleting a category that menu items are listed under asks where they go, so
 * none is left without one. An empty category is just confirmed.
 */
export function DeleteCategoryDialog(props: DeleteCategoryDialogProps) {
  // Remounted per category so the last choice does not carry over.
  return <DeleteCategoryForm key={props.category?.id ?? "none"} {...props} />;
}

function DeleteCategoryForm({
  category,
  others,
  onOpenChange,
  onConfirm,
  isPending = false,
  error = null,
}: DeleteCategoryDialogProps) {
  const { t } = useLingui();
  const [targetId, setTargetId] = useState("");

  if (!category) {
    return <AppDialog open={false} onOpenChange={onOpenChange}>{null}</AppDialog>;
  }

  const hasItems = category.itemCount > 0;
  const candidates = others.filter((c) => c.id !== category.id);
  const canConfirm = !hasItems || targetId !== "";

  return (
    <AppDialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      title={t`Delete category`}
      description={
        hasItems
          ? t`"${category.name}" has ${category.itemCount} menu items. Choose a category to move them to before deleting it.`
          : t`Delete "${category.name}"? No menu items are in it.`
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-(--color-danger-text)">{error ?? ""}</span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t`Cancel`}
            </Button>
            <Button
              variant="destructive"
              disabled={isPending || !canConfirm}
              onClick={() => onConfirm(category, hasItems ? Number(targetId) : undefined)}
            >
              {t`Delete category`}
            </Button>
          </div>
        </div>
      }
    >
      {hasItems &&
        (candidates.length > 0 ? (
          <div className="space-y-2">
            <Label htmlFor="reassign-category">{t`Move menu items to`}</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger id="reassign-category">
                <SelectValue placeholder={t`Select a category`} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-sm text-(--color-text-secondary)">
            {t`There is no other category to move them to. Add one first.`}
          </p>
        ))}
    </AppDialog>
  );
}
